import {Card, CardProps} from 'platform/components';
import {Grid, Heading, Space} from 'platform/foundation';

import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {selectAuditAssets, selectErrorCategories} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {AuditCategoryOfStructure} from '../../../types/AuditCategoryOfStructure';
import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {ParamType} from '../../../types/ParamType';
import {useConditionContext} from '../hooks/useConditionContext';
import {findByParamType} from '../utils/findByParamType';
import {getFormFieldName} from '../utils/getFormFieldName';
import {getTranslation} from '../utils/getTranslation';
import {resetCategoryFields} from '../utils/resetCategoryFields';
import {resetToggleRelatedFields} from '../utils/resetToggleRelatedFields';
import {AdditionalInformation} from './AdditionalInformation';
import {AuditFormField} from './AuditFormField';

interface CategoryCardProps {
  data: AuditCategoryOfStructure;
  cardVariant: string;
  additionalInformationBeside?: boolean;
  isNewAdded?: boolean;
  photoGrid?: number;
}

export const CategoryCard = ({
  data: category,
  additionalInformationBeside,
  cardVariant,
  isNewAdded,
  photoGrid,
  ...rest
}: CategoryCardProps & TestIdProps) => {
  const {isDisabledForUser} = useConditionContext();
  const locale = useSelector(selectUserSelectedLanguage);
  const errorCategories = useSelector(selectErrorCategories);

  const {
    formRenderProps: {
      Field,
      form: {
        mutators: {setFieldValue},
        getFieldState,
      },
    },
  } = useConditionContext();

  const name = getTranslation(locale, category.name);
  const relatedActions = category.paramDefinitions?.relatedActions;
  const additionalInformation = category.paramDefinitions?.additionalInformation;
  const isMandatory = category.mandatory;

  const additionalComment = findByParamType(additionalInformation, ParamType.ADDITIONAL_COMMENT);
  const additionalPhotos = findByParamType(additionalInformation, ParamType.ADDITIONAL_PHOTOS);
  const toggleAction = findByParamType(relatedActions, ParamType.TOGGLE);
  const fieldName = getFormFieldName(category.id, toggleAction?.id);

  const toggleValue = toggleAction?.values?.find((option) => option.value === 'true');
  const toggleContent = toggleValue?.content;
  const toggleAdditionalInformation = toggleContent?.additionalInformation;
  const toggleAdditionalPhotos = findByParamType(
    toggleAdditionalInformation,
    ParamType.ADDITIONAL_PHOTOS
  );

  const photoAssets = useSelector(selectAuditAssets(category.id, additionalPhotos?.id));
  const togglePhotoAssets = useSelector(selectAuditAssets(category.id, toggleAdditionalPhotos?.id));

  const hasPhotoError =
    errorCategories?.[category.id] && additionalPhotos?.mandatory && isNilOrEmpty(photoAssets);
  const hasTogglePhotoError =
    errorCategories?.[category.id] &&
    toggleAdditionalPhotos?.mandatory &&
    isNilOrEmpty(togglePhotoAssets);

  useEffect(
    function enableNewlyAddedCategory() {
      const fieldValue = getFieldState(fieldName)?.value;

      if (isNewAdded && fieldValue === undefined) {
        setFieldValue(fieldName, true);
      }
    },
    [isNewAdded] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  if (!category.visible) {
    return null;
  }

  const cardTitle = `${isMandatory ? '*' : ''} ${name}`;

  if (!toggleAction) {
    return (
      <Card
        variant={cardVariant as CardProps['variant']}
        data-testid={suffixTestId('categoryCard', rest)}
      >
        <Heading size={4} color={errorCategories?.[category.id] ? 'danger' : undefined}>
          {cardTitle}
        </Heading>
        <Space vertical={4} />
        <Grid columns={[1, 2]} verticalSpacing={0}>
          <ChildCategoriesRelatedActions
            data={category}
            data-testid={suffixTestId('categoryCard', rest)}
          />
          <RelatedActions
            data={relatedActions ?? []}
            category={category}
            data-testid={suffixTestId('categoryCard', rest)}
          />
        </Grid>
        <AdditionalInformation
          data-testid={suffixTestId('categoryCard', rest)}
          categoryId={category.id}
          additionalComment={additionalComment}
          additionalPhotos={additionalPhotos}
          beside={additionalInformationBeside}
          uniqueKey={category.uniqueKey}
          photoGrid={photoGrid}
          hasPhotoError={hasPhotoError}
        />
      </Card>
    );
  }

  const relatedActionsData = relatedActions?.filter(
    (relatedAction) => relatedAction.type !== ParamType.TOGGLE
  );

  return (
    <Field
      name={fieldName}
      component={({input}) => {
        const fieldValue = getFieldState(fieldName)?.value;
        const isChecked = typeof fieldValue === 'boolean' ? fieldValue : Boolean(input.value);

        const toggleAdditionalComment = findByParamType(
          toggleAdditionalInformation,
          ParamType.ADDITIONAL_COMMENT
        );

        const hasOnlyToggle =
          !toggleContent && !relatedActionsData?.length && !additionalInformation?.length;
        const isExpanded = !hasOnlyToggle ? isChecked : false;

        return (
          <Card
            isExpanded={isExpanded}
            variant={cardVariant as CardProps['variant']}
            title={cardTitle}
            control={{
              type: 'switch',
              isDisabled: isDisabledForUser,
              value: isChecked,
              onChange: (checked) => {
                if (!checked) {
                  resetToggleRelatedFields(category, toggleAction, setFieldValue);
                  category.childCategories?.forEach((childCategory) => {
                    resetCategoryFields(childCategory, setFieldValue);
                  });
                }
                input.onChange(checked);
              },
              'data-testid': suffixTestId('categoryCard', rest),
            }}
            data-testid={suffixTestId('categoryCard', rest)}
          >
            <Grid columns={[1, 2]} verticalSpacing={0}>
              <ChildCategoriesRelatedActions
                data={category}
                data-testid={suffixTestId('categoryCard', rest)}
              />
              <RelatedActions
                data={[...(toggleContent?.relatedActions ?? []), ...(relatedActionsData ?? [])]}
                category={category}
                data-testid={suffixTestId('categoryCard', rest)}
              />
            </Grid>
            {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
            {toggleAdditionalInformation && (
              <AdditionalInformation
                categoryId={category.id}
                additionalComment={toggleAdditionalComment}
                additionalPhotos={toggleAdditionalPhotos}
                beside={additionalInformationBeside}
                uniqueKey={category.uniqueKey}
                photoGrid={photoGrid}
                hasPhotoError={hasTogglePhotoError}
                data-testid={suffixTestId('categoryCard', rest)}
              />
            )}
            <AdditionalInformation
              categoryId={category.id}
              additionalComment={additionalComment}
              additionalPhotos={additionalPhotos}
              beside={additionalInformationBeside}
              uniqueKey={category.uniqueKey}
              photoGrid={photoGrid}
              hasPhotoError={hasPhotoError}
              data-testid={suffixTestId('categoryCard', rest)}
            />
          </Card>
        );
      }}
    />
  );
};

const ChildCategoriesRelatedActions = ({
  data: category,
  ...rest
}: Pick<CategoryCardProps, 'data'> & TestIdProps) => (
  <>
    {category.childCategories?.map((category) => {
      if (!category.visible) {
        return null;
      }

      const relatedActions: AuditParamDefinition[] = [];
      category.paramDefinitions?.relatedActions?.forEach((relatedAction) => {
        relatedActions.push({
          ...relatedAction,
          label: category.name,
        });
      });

      return relatedActions?.map((paramDefinition, index) => (
        <AuditFormField
          key={`related-action-${paramDefinition.id}-${category.id}`}
          paramDefinition={paramDefinition}
          categoryId={category.id}
          uniqueKey={category.uniqueKey}
          data-testid={suffixTestId(`childCategoriesRelatedActions-[${index}]`, rest)}
        />
      ));
    })}
  </>
);

interface RelatedActionsProps extends TestIdProps {
  category: AuditCategoryOfStructure;
  data?: Array<AuditParamDefinition>;
}

const RelatedActions = ({data, category, ...rest}: RelatedActionsProps) => (
  <>
    {data?.map((paramDefinition, index) => (
      <AuditFormField
        key={`relatedActions-${category.id}-${paramDefinition.id}`}
        paramDefinition={paramDefinition}
        categoryId={category.id}
        uniqueKey={category.uniqueKey}
        data-testid={suffixTestId(`relatedActions-[${index}]`, rest)}
      />
    ))}
  </>
);
